import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import SaleCard from '@/components/SaleCard';
import SaleDetailsModal from '@/components/SaleDetailsModal';
import { getMySales, getSaleDetails } from '@/api/sales';
import TabWithFilter from '@/components/TabWithFilter';
import Button from '@/components/Button';
import dondonGreyLogo from '@/assets/dondon_grey.svg';

const SalesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('ALL');
  const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
  const [cursor, setCursor] = useState(null);
  const [accumulatedSales, setAccumulatedSales] = useState([]);

  // State for specific sale detail modal
  const [specificSaleModalOpen, setSpecificSaleModalOpen] = useState(false);
  const [specificSaleId, setSpecificSaleId] = useState(null);

  const { data: salesData, isLoading, isError, isFetching } = useQuery({
    queryKey: ['sales', activeTab, dateRange.startDate, dateRange.endDate, cursor],
    queryFn: () => getMySales({
      status: activeTab,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      cursor
    })
  });

  // Query for specific sale details
  const { data: specificSaleData } = useQuery({
    queryKey: ['sale-details', specificSaleId],
    queryFn: () => getSaleDetails(specificSaleId),
    enabled: !!specificSaleId
  });

  // Update accumulated sales when new data arrives
  useEffect(() => {
    if (salesData?.data) {
      if (cursor) {
        // Append new sales to accumulated sales
        setAccumulatedSales(prev => [...prev, ...salesData.data]);
      } else {
        // Reset accumulated sales when filters change
        setAccumulatedSales(salesData.data);
      }
    }
  }, [salesData, cursor]);

  // Handle URL parameters for specific sale
  useEffect(() => {
    const saleId = searchParams.get('saleId');
    if (saleId) {
      setSpecificSaleId(saleId);
      setSpecificSaleModalOpen(true);
    }
  }, [searchParams]);

  // Handle location state for opening sale modal from notification
  useEffect(() => {
    const openSaleId = location.state?.openSaleId;
    if (openSaleId) {
      setSpecificSaleId(openSaleId);
      setSpecificSaleModalOpen(true);
      // Clear state from location to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const tabs = [
    { value: 'ALL', label: t('sales.tabs.all') },
    { value: 'PENDING_SHIPMENT', label: t('sales.tabs.pendingShipment') },
    { value: 'SHIPPED', label: t('sales.tabs.pendingVerification') },
    { value: 'FINISHED', label: t('sales.tabs.completed') }
  ];

  const handleSearch = (dateRange) => {
    setDateRange(dateRange);
    setCursor(null); // Reset pagination when filter changes
  };

  const handleReset = () => {
    setDateRange({ startDate: null, endDate: null });
    setCursor(null); // Reset pagination when filter changes
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setDateRange({ startDate: null, endDate: null }); // Reset date range when tab changes
    setCursor(null); // Reset pagination when tab changes
  };

  const handleLoadMore = () => {
    if (salesData?.cursor) {
      setCursor(salesData.cursor);
    }
  };

  const handleOpenSpecificSaleModal = (saleId) => {
    setSpecificSaleId(saleId);
    setSpecificSaleModalOpen(true);
    // Update URL to include saleId parameter
    setSearchParams({ saleId });
  };

  const handleCloseSpecificSaleModal = () => {
    setSpecificSaleModalOpen(false);
    setSpecificSaleId(null);
    // Remove saleId parameter from URL
    setSearchParams({});
  };

  const hasMoreSales = !!salesData?.cursor;

  // Determine if any filter is active
  const isAnyFilterActive = activeTab !== 'ALL' || dateRange.startDate || dateRange.endDate;

  // Determine if the filter UI (tabs and headers) should be shown
  const shouldShowFilterUI = isAnyFilterActive || accumulatedSales.length > 0;

  return (
    <div className="mx-auto bg-white rounded-lg shadow-sm p-6">
      {/* Header */}
      <h1 className="text-lg font-bold mb-6">{t('me.menu.sales')}</h1>

      {shouldShowFilterUI && (
        <TabWithFilter
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          onSearch={handleSearch}
          onReset={handleReset}
        />
      )}

      {/* Table Headers */}
      {shouldShowFilterUI && (
        <div className="mt-4 grid grid-cols-[1fr,108px,108px,108px,180px] gap-6 py-1.5 bg-[#f8f8f8] text-sm text-center text-gray-500">
          <div>{t('sales.columns.saleInfo')}</div>
          <div>{t('sales.columns.grade')}</div>
          <div>{t('sales.columns.price')}</div>
          <div>{t('sales.columns.status')}</div>
          <div>{t('sales.columns.operations')}</div>
        </div>
      )}

      {/* Sales List */}
      {isLoading && !cursor && !shouldShowFilterUI ? (
        <div className="h-[800px] flex justify-center items-center">
          <span className="text-gray-400">{t('common.loading')}</span>
        </div>
      ) : isLoading && !cursor && shouldShowFilterUI ? (
        <div className="w-full flex justify-center items-center py-10">
          <p>{t('common.loading')}</p>
        </div>
      ) : isError ? (
        <div className="w-full flex justify-center items-center py-10">
          <p>{t('common.error')}</p>
        </div>
      ) : accumulatedSales.length > 0 ? (
        <div className="divide-y divide-gray-200">
          {accumulatedSales.map((sale) => (
            <SaleCard
              key={sale.id}
              sale={sale}
              onWithdrawClick={() => navigate('/me/wallet?withdraw=true')}
              onStatusClick={handleOpenSpecificSaleModal}
            />
          ))}
        </div>
      ) : !shouldShowFilterUI ? (
        <div className="h-[800px] flex flex-col justify-center items-center gap-6">
          <img src={dondonGreyLogo} alt="DonDon" className="w-24 h-16 opacity-50" />
          <div className="text-center text-[#aaaaaa] text-sm font-normal leading-tight">
            {t('me.sales.empty_state_main')}
          </div>
        </div>
      ) : (
        <div className="w-full flex justify-center items-center py-10">
          <p>{t('me.sales.noSales')}</p>
        </div>
      )}

      {/* Loading indicator for pagination */}
      {isFetching && cursor && (
        <div className="w-full flex justify-center items-center py-4">
          <p>{t('common.loading')}</p>
        </div>
      )}

      {/* Load More Button */}
      {hasMoreSales && (
        <div className="flex justify-center mt-8">
          <Button 
            variant="default" 
            size="m" 
            onClick={handleLoadMore}
            disabled={isFetching}
          >
            {isFetching && cursor ? t('common.loading') : t('home.actions.load_more')}
          </Button>
        </div>
      )}

      {/* Specific Sale Details Modal */}
      {specificSaleModalOpen && specificSaleData?.data && (
        <SaleDetailsModal
          isOpen={specificSaleModalOpen}
          onClose={handleCloseSpecificSaleModal}
          sale={specificSaleData.data}
          openShippingPanel={false}
          onWithdrawClick={() => navigate('/me/wallet?withdraw=true')}
        />
      )}

    </div>
  );
};

export default SalesPage; 